import React, { useEffect, useState } from "react"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import { graphql } from "gatsby"
import { gql, useQuery } from "@apollo/client"
import { shuffle } from "lodash"
import Big from "big.js"
import {
  Heading,
  ProductDetails,
  Banner,
} from "@gorilla-technologies/customer-ui"
import { Layout, Section, ProductList } from "../components"
import { useWallet } from "../providers"
import { Product } from "../types.d"
import { getCountryCode } from "../services/getCountryCode"
import { storeTxValuesInSanity } from "../services/storeTxValuesInSanity"
import { getLiveProducts } from "../services/getLiveProducts"

export type ProductProps = {
  data: {
    gorillaProduct: {
      nodes: Product[]
    }
    allGorillaProduct: {
      nodes: Product[]
    }
  }
}

Big.PE = 36

export const GET_TOKEN_INFORMATION = gql`
  query GET_TOKEN_INFORMATION(
    $account: String!
    $lastDate: timestamptz!
    $store: String!
  ) {
    ownToken: token(
      where: {
        lastTransferred: { _lt: $lastDate }
        ownerId: { _eq: $account }
        _and: { burnedAt: { _is_null: true }, id: { _regex: $store } }
      }
      order_by: { lastTransferred: desc }
    ) {
      id
      thingId
    }
  }
`

const isSSR = typeof window === "undefined"

// Checks if a certain query param is set
const hasQueryParam = param => {
  if (isSSR) return false

  return new URLSearchParams(window.location.search).has(param)
}

const ProductPage = ({ data }: ProductProps) => {
  const [stored, setStored] = useState(false)
  const [isOwnedByUser, setIsOwnedByUser] = useState(false)
  const [isOutOfStock, setIsOutofStock] = useState(false)
  const [isFetchingProducts, setIsFetchingProducts] = useState(true)
  const [tokenId, setTokenId] = useState("")
  // Get all data for the current product
  const { nodes: singleProductArray } = data.gorillaProduct
  const { localFile, ...product } = singleProductArray[0]
  const { mintbaseThingId } = product

  // Recommended products are 8 random products (excluding the current one)
  const { nodes: products } = data.allGorillaProduct
  const recommendedProducts = shuffle(products).slice(0, 8)

  // Wallet setup
  const { wallet, isLoading } = useWallet()
  const isConnected = wallet?.isConnected()
  const account = wallet?.activeAccount?.accountId

  const {
    loading,
    error,
    data: graphqlData,
  } = useQuery(GET_TOKEN_INFORMATION, {
    variables: {
      account,
      lastDate: "now()",
      store: process.env.GATSBY_STORE_NAME,
    },
    notifyOnNetworkStatusChange: true,
    skip: !account,
  })
  if (error) console.warn(error.message)

  useEffect(() => {
    if (!loading && graphqlData && mintbaseThingId) {
      setIsOwnedByUser(
        Boolean(
          graphqlData.ownToken.find(
            ({ thingId }) => thingId === mintbaseThingId
          )
        )
      )
    }
  }, [graphqlData, loading, mintbaseThingId, product.tokens])

  useEffect(() => {
    const updateProducts = async () => {
      const nftLiveProducts = await getLiveProducts()
      setIsFetchingProducts(false)

      const nftLiveProduct = nftLiveProducts.filter(
        (item: any) => item.mintbaseThingId === mintbaseThingId
      )
      setTokenId(nftLiveProduct[0].tokens[0].id)
      setIsOutofStock(Boolean(nftLiveProduct[0].inStock === 0))
    }
    updateProducts()
  }, [mintbaseThingId])

  const showPurchaseConfirmation = Boolean(
    hasQueryParam("purchase") && isOwnedByUser
  )

  const storeValues = async (purchaseHash: String) => {
    const code = await getCountryCode()
    await storeTxValuesInSanity(account, purchaseHash, code)
  }

  if (showPurchaseConfirmation && !stored && account) {
    const purchaseHash = new URLSearchParams(window.location.search).get(
      "transactionHashes"
    )
    storeValues(purchaseHash)
    setStored(true)
  }

  const buyNFT = async (nftProduct: any) => {
    if (!wallet || !wallet.minter) return
    const price = new Big(nftProduct.price.inYocto)
    const newPrice = price.toString()
    const tokenKey = tokenId
    await wallet.makeOffer(tokenKey, newPrice)
  }

  const productImage = ["image/gif", "image/svg+xml"].includes(
    product.productType
  ) ? (
    <img src={product.url} alt={product.title} className="max-h-[500px]" />
  ) : (
    <GatsbyImage image={getImage(localFile)} alt={product.title} />
  )

  return (
    <Layout
      title={product.title}
      removeTopPadding={!showPurchaseConfirmation}
      navbarBgColor="brand"
    >
      {showPurchaseConfirmation && (
        <Section width="large" gap>
          <Banner
            text="You have successfully purchased this item!"
            kind="success"
          />
        </Section>
      )}
      <Section width="full" horizontalPadding={false}>
        <ProductDetails
          product={product}
          image={productImage}
          isFetchingProducts={isFetchingProducts}
          onProductPurchase={() => {
            window.history.replaceState(null, null, "?purchase")
            buyNFT(product)
          }}
          isWalletConnected={isConnected}
          isWalletLoading={isLoading}
          connectWallet={() => {
            wallet?.connect({ requestSignIn: true })
          }}
          ownedByUser={isOwnedByUser}
          outOfStock={isOutOfStock}
        />
      </Section>
      <Section width="large" gap>
        <Heading level={2} align="left">
          More like this
        </Heading>
        <ProductList products={recommendedProducts} columns={4} />
      </Section>
    </Layout>
  )
}

export const query = graphql`
  query SingleProductQuery($id: String) {
    gorillaProduct: allGorillaProduct(filter: { id: { eq: $id } }) {
      nodes {
        title
        description
        productType
        url
        inStock
        id
        mintbaseThingId
        tokens {
          id
        }
        price {
          inNear
          inYocto
        }
        localFile {
          childImageSharp {
            gatsbyImageData(placeholder: NONE, height: 500)
          }
        }
      }
    }
    allGorillaProduct(filter: { id: { ne: $id } }) {
      nodes {
        title
        description
        productType
        id
        url
        price {
          inNear
        }
        localFile {
          childImageSharp {
            gatsbyImageData(placeholder: NONE, width: 320)
          }
        }
      }
    }
  }
`

export default ProductPage
